// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

module.exports = ({ searchTerm, iconState, searchResultsBlock, locale, skinUrl, actionUrl }) => {
    const searchActionDelay = 200;
    let timer;
    let enterPressed = false;
    let pendingRequest;

    const lmsg = localeKey => locale[localeKey].escapeHTML();

    const fatalError = message => {
        // eslint-disable-next-line no-alert
        alert(message);
    };

    const onPasteHandler = () => scheduleSearch();

    const onKeyUpHandler = event => {
        if ([Jsw.keyCode.UP_ARROW, Jsw.keyCode.DOWN_ARROW, Jsw.keyCode.LEFT_ARROW, Jsw.keyCode.RIGHT_ARROW, Jsw.keyCode.ENTER].indexOf(event.keyCode) !== -1) {
            return;
        }

        scheduleSearch();
    };

    const onKeyDownHandler = event => {
        if ([Jsw.keyCode.UP_ARROW, Jsw.keyCode.DOWN_ARROW].indexOf(event.keyCode) !== -1) {
            onArrowKeyPressed(event.keyCode);
        }

        enterPressed = (Jsw.keyCode.ENTER === event.keyCode);
        if (enterPressed) {
            scheduleSearch();
        }
    };

    const onFocusHandler = () => {
        searchTerm.value = '';
        searchTerm.removeClassName('search-empty');
    };

    const onBlurHandler = () => {
        // workaround for Google Chrome
        setTimeout(function () {
            showSearchFieldStub();
            setTimeout(resetSearch, 300);
        }, 0);
    };

    const showSearchFieldStub = () => {
        searchTerm.value = '';
        searchTerm.blur();
    };

    const scheduleSearch = () => {
        iconState.removeClassName('icon-search');
        iconState.addClassName('icon-indicator');

        abortPreviousSearch();
        timer = setTimeout(findTerm, searchActionDelay);
    };

    const abortPreviousSearch = () => {
        if (pendingRequest) {
            const abort = pendingRequest.abort.bind(pendingRequest);
            // global state is cleared first due to onSearchComplete callback
            pendingRequest = null;
            abort();
        }

        if (timer) {
            clearTimeout(timer);
        }
    };

    const onSearchSuccess = response => {
        if (searchTerm.value !== response.request.options.parameters.term) {
            return;
        }

        let data;
        try {
            data = response.responseText.evalJSON();
        } catch (e) {
            fatalError(`Failed to parse JSON response: ${e.message}`);
            return;
        }

        if ('error' === data.status) {
            let result = '';
            data.statusMessages.each(function (message) {
                result += `${message.title}: ${message.content}\n`;
            });
            fatalError(result);
            return;
        }

        if (enterPressed) {
            goToSelectedItem(data);
        } else {
            showResults(data);
        }
    };

    const onSearchFailure = response => {
        fatalError(`Search request failed due to following error: ${response.responseText}`);
    };

    const onSearchComplete = response => {
        if (!response || pendingRequest === response.request) {
            iconState.removeClassName('icon-indicator');
            iconState.addClassName('icon-search');
        }
    };

    const goToSelectedItem = results => {
        let activeRow = searchResultsBlock.down('li.active');

        if (!activeRow) {
            activeRow = searchResultsBlock.down('li');
        }

        if (activeRow && activeRow.down('a')) {
            Jsw.redirect(activeRow.down('a').href, null, activeRow.down('a').target);
        } else if (results.records.length) {
            Jsw.redirect(results.records[0].link, null, results.records[0].target);
        } else {
            showResults(results);
        }
    };

    const showResults = results => {
        let resultsHtml = '';

        if (0 === results.records.size()) {
            resultsHtml += `<li><div class="search-results-note">${lmsg('nothingFound')}</div></li>`;
        } else {
            results.records.each(function (item) {
                const itemDetails = ('undefined' !== typeof item.details) ? item.details.escapeHTML() : '';
                let linkTarget = '';
                if (item.target) {
                    linkTarget = `target="${item.target}"`;
                }
                const iconUrl = (
                    !item.icon ||
                    item.icon.startsWith(skinUrl) ||
                    item.icon.startsWith('http://') ||
                    item.icon.startsWith('https://') ||
                    item.icon.startsWith('/modules/')
                ) ? item.icon : `${skinUrl}${item.icon}`;

                resultsHtml +=
                    `<li>
                        <a href="${item.link.escapeHTML()}" title="${itemDetails}" ${linkTarget}>
                            ${iconUrl ? `<i class="icon-" style="background-image: url(${iconUrl.escapeHTML()})"></i>` : ''}
                            ${item.title.escapeHTML()}
                        </a>
                    </li>`;
            });

            if (results.meta.moreResultsFound) {
                resultsHtml +=
                    `<li>
                        <div class="search-results-note">${lmsg('moreResultsFound')}</div>
                    </li>`;
            }
        }

        searchResultsBlock.update(resultsHtml);

        const links = searchResultsBlock.select('a');
        links.each(function (link) {
            link.observe('mouseover', onItemMouseOver.bind(link));
            link.observe('mouseout', onItemMouseOut.bind(link));
        });
        if (0 !== links.size()) {
            links.first().up('li').addClassName('active');
        }

        searchResultsBlock.up('.dropdown').addClassName('open');
    };

    const onItemMouseOver = function () {
        const oldActiveLink = searchResultsBlock.select('li.active')[0];

        if (oldActiveLink) {
            oldActiveLink.removeClassName('active');
        }

        this.up('li').addClassName('active');
    };

    const onItemMouseOut = function () {
        this.up('li').removeClassName('active');
    };

    const resetSearch = () => {
        searchResultsBlock.up('.dropdown').removeClassName('open');

        abortPreviousSearch();
        onSearchComplete();
    };

    const onArrowKeyPressed = keyCode => {
        if (0 === searchResultsBlock.select('a').length) {
            return;
        }

        const activeRow = searchResultsBlock.down('li.active');

        if (!activeRow) {
            searchResultsBlock.down('li').addClassName('active');
            return;
        }

        if (Jsw.keyCode.DOWN_ARROW === keyCode) {
            const nextRow = activeRow.next();

            if (nextRow && nextRow.down('a')) {
                activeRow.removeClassName('active');
                nextRow.addClassName('active');
            }
        }

        if (Jsw.keyCode.UP_ARROW === keyCode) {
            const prevRow = activeRow.previous();

            if (prevRow && prevRow.down('a')) {
                activeRow.removeClassName('active');
                prevRow.addClassName('active');
            }
        }
    };

    const findTerm = () => {
        const term = searchTerm.value;

        if ('' === term) {
            resetSearch();
            return;
        }

        pendingRequest = new Ajax.Request(
            actionUrl,
            {
                method: 'get',
                parameters: { term },
                onSuccess: onSearchSuccess,
                onFailure: onSearchFailure,
                onComplete: onSearchComplete,
            }
        );
    };

    searchTerm.observe('paste', onPasteHandler);
    searchTerm.observe('keyup', onKeyUpHandler);
    searchTerm.observe('keydown', onKeyDownHandler);
    searchTerm.observe('focus', onFocusHandler);
    searchTerm.observe('blur', onBlurHandler);
};



// WEBPACK FOOTER //
// ./app/search.js